'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';

import { useCurrency } from '@/hooks/useCurrency';
import { STATES_ABBR_TO_FULL } from '@/models/State';

import {
  DATA_LAST_UPDATED,
  DISCLOSURE_ITC,
  DISCLOSURE_NET_METERING,
  PROJECTION_YEARS,
  SOLAR_USD_PER_WATT,
  stateIncentiveLinks,
} from '../data/content';
import { ProjectionScenario, scenarioProseLabel } from '../data/rateProjections';
import { Ownership, ProjectionResult } from '../engine';

import { formatBenchmarkDate, kwWithArticle, roundedPercent, roundFiveHundred } from './chartFormat';
import { CumulativeCostChart } from './CumulativeCostChart';
import { ReadoutStrip } from './ReadoutStrip';

type Props = {
  ownership: Ownership | null;
  result: ProjectionResult;
  scenario: ProjectionScenario;
  stateCode: string;
};

const UTILITY_COLOR = 'uiChartGas';
const SOLAR_COLOR = 'uiChartElectric';

// shortLabel only where the full one won't fit 375px — SVG-adjacent labels
// can't reflow, so the swap is a display toggle on two nodes
const LegendLine = ({ color, label, shortLabel }: { color: string; label: string; shortLabel?: string }) => (
  <DS.Flex alignItems="center" flexDirection="row" gap={1} w="fit-content">
    <x.span bg={color} borderRadius={2} display="inline-block" h="2px" w="14px" />
    <DS.Text color="uiHelperCopy" display={shortLabel ? { _: 'none', md: 'block' } : 'block'} text="body12">
      {label}
    </DS.Text>
    {shortLabel ? (
      <DS.Text color="uiHelperCopy" display={{ _: 'block', md: 'none' }} text="body12">
        {shortLabel}
      </DS.Text>
    ) : null}
  </DS.Flex>
);

// An open high end reads "16+ yrs", never "16–20 yrs": a cautious walk that
// never crossed inside the horizon has no upper bound to quote
const paybackCopy = ({ high, low }: NonNullable<ProjectionResult['paybackYearsRange']>): string =>
  high === null ? `${Math.round(low)}+ yrs` : `${Math.round(low)}–${Math.round(high)} yrs`;

// Tier 2: a separate calculation on labelled state averages, below a seam. A
// renter's bill is still their bill, so tier 1 stands for them — but a rooftop
// comparison is not theirs to act on.
export const SolarComparison = ({ ownership, result, scenario, stateCode }: Props) => {
  const { formatCurrency, formatPercent } = useCurrency();
  const { centsPerKwh, crossoverYear, escalationRate, paybackYearsRange, savingsRange, systemSizeKw, years } = result;

  if (ownership === 'RENT') {
    return null;
  }

  const seamLabel = `Solar in ${STATES_ABBR_TO_FULL[stateCode] ?? stateCode}`;
  const incentiveLink = stateIncentiveLinks[stateCode];
  // One string, not interleaved JSX: a text chunk that both opens with a space
  // and wraps loses that space in the SWC transform, silently closing "$2.80 a watt"
  const assumptions =
    `This is a simulation, not an estimate. It assumes ${kwWithArticle(systemSizeKw)} system at ` +
    `${formatCurrency(SOLAR_USD_PER_WATT, 2)} per watt, your state’s average residential rate ` +
    `(${centsPerKwh.toFixed(1)}¢/kWh), and the forecast view selected above ` +
    `(${scenarioProseLabel[scenario]}, ${roundedPercent(escalationRate, formatPercent)}/yr). ` +
    `Switch views and these two figures move with the chart.`;

  return (
    <DS.Flex aria-label={seamLabel} as="section" gap={5} textAlign="left">
      <DS.Flex alignItems="center" flexDirection="row" gap={4}>
        <x.span bg="uiDivider" flex={1} h="1px" />
        <DS.Text color="uiHelperCopy" letterSpacing="0.08em" text="body12">
          {seamLabel}
        </DS.Text>
        <x.span bg="uiDivider" flex={1} h="1px" />
      </DS.Flex>

      <DS.Flex gap={3}>
        <x.h2 text="h2">How solar can help, based on your state’s averages</x.h2>
        <DS.Text as="p" color="uiBodyCopy" text={{ _: 'body16', md: 'body20' }}>
          Everything above is your bill. Now let’s see how a solar system sized for your usage, priced at state and
          national averages, can cut that utility bill.
        </DS.Text>
      </DS.Flex>

      <DS.Flex as="figure" gap={3} m={0} mx="auto" w="fit-content">
        <DS.Text as="figcaption" color="uiHelperCopy" text="body14" textAlign="center">
          Total electricity cost over {PROJECTION_YEARS} years (state averages)
        </DS.Text>
        <CumulativeCostChart crossoverYear={crossoverYear} years={years} />
        <DS.Flex flexDirection="row" flexWrap="wrap" gap={4} justifyContent="center">
          <LegendLine color={UTILITY_COLOR} label="Utility only" />
          <LegendLine color={SOLAR_COLOR} label="Utility plus a solar system" shortLabel="Plus solar" />
        </DS.Flex>
      </DS.Flex>

      <ReadoutStrip
        cells={[
          {
            label: 'Typical payback, state averages',
            value: paybackYearsRange ? paybackCopy(paybackYearsRange) : `${PROJECTION_YEARS}+ yrs`,
          },
          {
            label: `${PROJECTION_YEARS}-year difference, state averages`,
            value: `${formatCurrency(roundFiveHundred(savingsRange.low))}–${formatCurrency(roundFiveHundred(savingsRange.high))}`,
          },
        ]}
      />

      {/* px matches the strip's cell inset so the small print lines up with the labels above */}
      <DS.Text as="p" color="uiHelperCopy" px={6} text="body12">
        {assumptions} {DISCLOSURE_ITC} {DISCLOSURE_NET_METERING}{' '}
        {incentiveLink ? (
          <>
            <a href={incentiveLink} rel="noreferrer" target="_blank">
              See current incentive programs in your state
            </a>{' '}
          </>
        ) : null}
        Rate and cost benchmarks last updated {formatBenchmarkDate(DATA_LAST_UPDATED)}.
      </DS.Text>
    </DS.Flex>
  );
};
